<template>
  <VCard class="google-signin mx-auto py-4" style="max-width:480px">
    <VCardText class="text-xs-center">
      <img :src="logo" alt="logo" class="mb-2" style="height:48px" />
      <h3 class="my-3">
        Sign into the WCI Dashboard
      </h3>
      <VDivider class="my-3"></VDivider>

      <div v-if="!isAuthenticated" class="pa-3">
        <div class="grey--text mb-3 caption"
          >Note: As of Sept, 2025 login will be handled by your Google
          account.</div
        >
        <VBtn
          large
          color="primary"
          :loading="isLoading"
          :disabled="!clientId || isLoading"
          @click="startAuth"
        >
          {{ isLoading ? 'Authenticating...' : 'Sign in with Google' }}
        </VBtn>
      </div>

      <div v-else class="auth-success">
        <VIcon color="success" class="mb-2">mdi-check-circle</VIcon>
        <div class="mb-2">
          <strong>{{ userProfile.name }}</strong
          ><br />
          <small>{{ userProfile.email }}</small>
        </div>
        <VBtn small color="error" @click="signOut">Sign Out</VBtn>
      </div>

      <div v-if="!clientId" class="mt-3 grey--text text--darken-1">
        <small>
          Developer note: set <code>VUE_APP_GOOGLE_OAUTH_CLIENT_ID</code> to
          enable Google Sign-in.
        </small>
      </div>

      <!-- Error display -->
      <VAlert v-if="error" type="error" class="mt-3 text-left">
        {{ error }}
      </VAlert>
    </VCardText>
  </VCard>
</template>

<script>
// Secure Google OAuth2 sign-in with Authorization Code + PKCE flow
// Educational component for development and testing

import { VCard, VCardText } from '@vuetify/VCard'
import VBtn from '@vuetify/VBtn/'
import VIcon from '@vuetify/VIcon'
import VAlert from '@vuetify/VAlert'

export default {
  name: 'GoogleSignIn',
  components: {
    VCard,
    VCardText,
    VBtn,
    VIcon,
    VAlert,
  },
  data() {
    return {
      clientId: process.env.VUE_APP_GOOGLE_OAUTH_CLIENT_ID || null,
      redirectUri: window.location.origin + '/auth/callback',
      logo: require('@assets/logo.png'),
      isLoading: false,
      isAuthenticated: false,
      userProfile: null,
      error: null,
      codeVerifier: null,
      state: null,
    }
  },
  computed: {
    isGoogleAuthenticated() {
      return this.$store.getters.isAuthenticated
    },
  },
  mounted() {
    // Only check auth callback if we're not on the dedicated callback route
    if (this.$route.path !== '/auth/callback') {
      this.checkAuthCallback()
    }
    // Check existing authentication state
    this.checkExistingAuth()
  },
  methods: {
    async startAuth() {
      if (!this.clientId) {
        this.error = 'VUE_APP_GOOGLE_OAUTH_CLIENT_ID not configured'
        return
      }

      this.isLoading = true
      this.error = null

      try {
        // Generate PKCE parameters
        const { codeVerifier, codeChallenge } = await this.generatePKCE()
        this.codeVerifier = codeVerifier

        // Generate state parameter for CSRF protection
        this.state = this.generateRandomString(32)

        // Store PKCE and state in sessionStorage for callback
        sessionStorage.setItem('oauth_code_verifier', codeVerifier)
        sessionStorage.setItem('oauth_state', this.state)

        const scope = 'openid email profile'
        const params = new URLSearchParams({
          client_id: this.clientId,
          redirect_uri: this.redirectUri,
          response_type: 'code', // Authorization Code flow
          scope: scope,
          state: this.state,
          code_challenge: codeChallenge,
          code_challenge_method: 'S256',
          prompt: 'select_account',
        })

        const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`

        // Redirect to Google OAuth
        window.location.href = authUrl
      } catch (error) {
        console.error('Auth start error:', error)
        this.error = 'Failed to start authentication: ' + error.message
        this.isLoading = false
      }
    },

    async checkAuthCallback() {
      // Check if we're on the callback URL with authorization code
      const urlParams = new URLSearchParams(window.location.search)
      const code = urlParams.get('code')
      const state = urlParams.get('state')
      const error = urlParams.get('error')

      if (error) {
        this.error = `OAuth error: ${error} - ${urlParams.get(
          'error_description'
        )}`
        this.cleanupCallback()
        return
      }

      if (code && state) {
        await this.handleAuthCallback(code, state)
      }
    },

    async handleAuthCallback(code, state) {
      this.isLoading = true

      try {
        // Verify state parameter (CSRF protection)
        const storedState = sessionStorage.getItem('oauth_state')
        if (state !== storedState) {
          throw new Error('Invalid state parameter - possible CSRF attack')
        }

        // Get stored code verifier
        const codeVerifier = sessionStorage.getItem('oauth_code_verifier')
        if (!codeVerifier) {
          throw new Error(
            'Missing code verifier - authentication flow corrupted'
          )
        }

        // Exchange authorization code for tokens
        const response = await fetch('/.netlify/functions/verifyGoogleLogin', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            code: code,
            redirectUri: this.redirectUri,
            codeVerifier: codeVerifier,
          }),
        })

        const result = await response.json()

        if (!response.ok) {
          // Use the detailed error message from the lambda if available
          const errorMessage = result.details || result.error || 'Token exchange failed'
          throw new Error(errorMessage)
        }

        if (result.payload) {
          const userProfile = {
            email: result.payload.email,
            name: result.payload.name,
            given_name: result.payload.given_name,
            family_name: result.payload.family_name,
            sub: result.payload.sub,
            roles: result.payload.roles || [],
            permissions: result.payload.permissions || {},
            metadata: result.payload.metadata || {},
            customAttributes: result.payload.customAttributes || {},
          }

          // Update local state
          this.userProfile = userProfile
          this.isAuthenticated = true

          // Store in Vuex store
          this.$store.commit('GOOGLE_AUTH_LOGIN', {
            user: userProfile,
            timestamp: Date.now(),
            sessionToken: result.sessionToken,
          })

          // Emit event for parent components
          this.$emit('authenticated', userProfile)
        }
      } catch (error) {
        console.error('Auth callback error:', error)
        // Display user-friendly error message
        this.error = error.message.startsWith('Authentication failed:') ? 
                     error.message : 
                     error.message
      } finally {
        this.isLoading = false
        this.cleanupCallback()
      }
    },

    cleanupCallback() {
      // Clean up URL and session storage
      sessionStorage.removeItem('oauth_code_verifier')
      sessionStorage.removeItem('oauth_state')

      // Remove query parameters from URL without reloading
      if (window.location.search) {
        const url = new URL(window.location)
        url.search = ''
        window.history.replaceState({}, document.title, url.pathname)
      }
    },

    async checkExistingAuth() {
      // Check if user is already authenticated via consolidated user store
      if (this.$store.getters.isAuthenticated) {
        this.isAuthenticated = true
        this.userProfile = this.$store.getters.user
      }
    },

    async signOut() {
      // Clear local state
      this.isAuthenticated = false
      this.userProfile = null
      this.error = null

      // Clear authentication from Vuex store
      this.$store.dispatch('logout')

      this.$emit('signed-out')
    },

    // PKCE utility functions
    async generatePKCE() {
      const codeVerifier = this.generateRandomString(128)
      const codeChallenge = await this.generateCodeChallenge(codeVerifier)
      return { codeVerifier, codeChallenge }
    },

    generateRandomString(length) {
      const charset =
        'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~'
      let result = ''
      for (let i = 0; i < length; i++) {
        result += charset.charAt(Math.floor(Math.random() * charset.length))
      }
      return result
    },

    async generateCodeChallenge(codeVerifier) {
      const encoder = new TextEncoder()
      const data = encoder.encode(codeVerifier)
      const digest = await window.crypto.subtle.digest('SHA-256', data)
      return btoa(String.fromCharCode(...new Uint8Array(digest)))
        .replace(/=/g, '')
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
    },
  },
}
</script>
